import type {
  ConsumerContractRegistryManifest,
  FlatSubscriptionRow,
  RiskClassification,
  ChangePropagation,
} from './consumerContractRegistry.types';
import {
  buildFieldIndex,
  flattenManifestSubscriptions,
  labelForChangePropagation,
  labelForRiskClassification,
  labelForRoute,
} from './consumerContractRegistry.utils';

/** Operation risk is green only when expected_neutral. */
export function isGreenOperationRisk(risk: RiskClassification): boolean {
  return risk === 'expected_neutral';
}

/** Propagation is green when shared automatically, an accepted contract boundary, or a stored cached read (PNG URL). */
export function isGreenPropagation(propagation: ChangePropagation): boolean {
  return (
    propagation === 'automatic_shared' ||
    propagation === 'generated_from_shared_contract' ||
    propagation === 'no_propagation_required'
  );
}

/** Subscription appears non-green in the field index when either badge would not be success. */
export function subscriptionNeedsAttention(row: FlatSubscriptionRow): boolean {
  return !isGreenOperationRisk(row.riskClassification) || !isGreenPropagation(row.changePropagation);
}

export interface RegistryAttentionDigest {
  manifestVersion: string;
  lastReviewedAt: string;
  generatedAt: string;
  summary: {
    totalSubscriptions: number;
    attentionSubscriptions: number;
    nonGreenOperationRisk: number;
    nonGreenPropagation: number;
    globalWarnings: number;
    artifactWarnings: number;
    fieldHotspots: number;
  };
  globalWarnings: Array<{ id: string; severity: string; message: string; relatedSubscriptionIds?: string[] }>;
  artifactWarnings: Array<{ artifactId: string; artifactName: string; id: string; severity: string; message: string }>;
  subscriptions: Array<{
    subscriptionId: string;
    artifactId: string;
    artifactName: string;
    fieldKey: string;
    subscribedField: string;
    operationRisk: RiskClassification;
    changePropagation: ChangePropagation;
    route: string;
    riskRationale: string;
    propagationRationale: string;
    reasons: string[];
  }>;
  fieldHotspots: Array<{
    fieldKey: string;
    subscribedField: string;
    subscribers: Array<{
      artifactName: string;
      operationRisk: RiskClassification;
      changePropagation: ChangePropagation;
      reasons: string[];
    }>;
  }>;
}

function attentionReasons(row: FlatSubscriptionRow): string[] {
  const reasons: string[] = [];
  if (!isGreenOperationRisk(row.riskClassification)) {
    reasons.push(`Operation risk: ${labelForRiskClassification(row.riskClassification)}`);
  }
  if (!isGreenPropagation(row.changePropagation)) {
    reasons.push(`Propagation: ${labelForChangePropagation(row.changePropagation)}`);
  }
  return reasons;
}

export function buildRegistryAttentionDigest(
  manifest: ConsumerContractRegistryManifest,
): RegistryAttentionDigest {
  const flat = flattenManifestSubscriptions(manifest);
  const attentionRows = flat.filter(subscriptionNeedsAttention);

  const artifactWarnings: RegistryAttentionDigest['artifactWarnings'] = [];
  for (const artifact of manifest.artifacts) {
    for (const w of artifact.warnings || []) {
      artifactWarnings.push({
        artifactId: artifact.id,
        artifactName: artifact.name,
        id: w.id,
        severity: w.severity,
        message: w.message,
      });
    }
  }

  const subscriptions = attentionRows.map((row) => ({
    subscriptionId: `${row.artifactId}.${row.id}`,
    artifactId: row.artifactId,
    artifactName: row.artifactName,
    fieldKey: row.fieldKey,
    subscribedField: row.subscribedField,
    operationRisk: row.riskClassification,
    changePropagation: row.changePropagation,
    route: labelForRoute(row.subscriptionKind, row.viaBuilder),
    riskRationale: row.riskRationale,
    propagationRationale: row.propagationRationale,
    reasons: attentionReasons(row),
  }));

  const fieldHotspots: RegistryAttentionDigest['fieldHotspots'] = [];
  for (const entry of buildFieldIndex(manifest)) {
    const flagged = entry.subscribers.filter(subscriptionNeedsAttention);
    if (flagged.length === 0) continue;
    fieldHotspots.push({
      fieldKey: entry.fieldKey,
      subscribedField: entry.subscribedField,
      subscribers: flagged.map((row) => ({
        artifactName: row.artifactName,
        operationRisk: row.riskClassification,
        changePropagation: row.changePropagation,
        reasons: attentionReasons(row),
      })),
    });
  }

  return {
    manifestVersion: manifest.manifestVersion,
    lastReviewedAt: manifest.lastReviewedAt,
    generatedAt: new Date().toISOString(),
    summary: {
      totalSubscriptions: flat.length,
      attentionSubscriptions: attentionRows.length,
      nonGreenOperationRisk: flat.filter((r) => !isGreenOperationRisk(r.riskClassification)).length,
      nonGreenPropagation: flat.filter((r) => !isGreenPropagation(r.changePropagation)).length,
      globalWarnings: manifest.globalWarnings.length,
      artifactWarnings: artifactWarnings.length,
      fieldHotspots: fieldHotspots.length,
    },
    globalWarnings: manifest.globalWarnings.map((w) => ({
      id: w.id,
      severity: w.severity,
      message: w.message,
      relatedSubscriptionIds: w.relatedSubscriptionIds,
    })),
    artifactWarnings,
    subscriptions,
    fieldHotspots,
  };
}

export function formatRegistryAttentionDigestMarkdown(digest: RegistryAttentionDigest): string {
  const lines: string[] = [
    '# Consumer Contract Registry — Attention Digest',
    '',
    `Generated: ${digest.generatedAt}`,
    `Manifest: ${digest.manifestVersion} · Last reviewed: ${digest.lastReviewedAt}`,
    '',
    'Non-green items are subscriptions where operation risk is not **Expected**, and/or propagation is not **Automatic / shared**.',
    '',
    '## Summary',
    '',
    `- Total subscriptions: ${digest.summary.totalSubscriptions}`,
    `- Subscriptions needing attention: ${digest.summary.attentionSubscriptions}`,
    `- Non-green operation risk: ${digest.summary.nonGreenOperationRisk}`,
    `- Non-green propagation: ${digest.summary.nonGreenPropagation}`,
    `- Global warnings: ${digest.summary.globalWarnings}`,
    `- Artifact warnings: ${digest.summary.artifactWarnings}`,
    `- Field hotspots (≥1 non-green subscriber): ${digest.summary.fieldHotspots}`,
    '',
  ];

  if (digest.globalWarnings.length > 0) {
    lines.push('## Global warnings', '');
    for (const w of digest.globalWarnings) {
      lines.push(`### ${w.id} (${w.severity})`, '', w.message, '');
      if (w.relatedSubscriptionIds?.length) {
        lines.push(`Related: ${w.relatedSubscriptionIds.join(', ')}`, '');
      }
    }
  }

  if (digest.artifactWarnings.length > 0) {
    lines.push('## Artifact warnings', '');
    for (const w of digest.artifactWarnings) {
      lines.push(`### ${w.artifactName} — ${w.id} (${w.severity})`, '', w.message, '');
    }
  }

  if (digest.fieldHotspots.length > 0) {
    lines.push('## Field hotspots', '');
    for (const field of digest.fieldHotspots) {
      lines.push(`### ${field.subscribedField} (\`${field.fieldKey}\`)`, '');
      for (const sub of field.subscribers) {
        lines.push(
          `- **${sub.artifactName}** — ${sub.reasons.join('; ')}`,
        );
      }
      lines.push('');
    }
  }

  if (digest.subscriptions.length > 0) {
    lines.push('## All non-green subscriptions', '');
    const byArtifact = new Map<string, typeof digest.subscriptions>();
    for (const sub of digest.subscriptions) {
      const list = byArtifact.get(sub.artifactName) || [];
      list.push(sub);
      byArtifact.set(sub.artifactName, list);
    }
    for (const [artifactName, subs] of Array.from(byArtifact.entries()).sort((a, b) => a[0].localeCompare(b[0]))) {
      lines.push(`### ${artifactName}`, '');
      for (const sub of subs.sort((a, b) => a.subscribedField.localeCompare(b.subscribedField))) {
        lines.push(
          `- **${sub.subscribedField}** (\`${sub.subscriptionId}\`)`,
          `  - Route: ${sub.route}`,
          `  - ${sub.reasons.join('; ')}`,
          `  - Risk rationale: ${sub.riskRationale}`,
          `  - Propagation rationale: ${sub.propagationRationale}`,
        );
      }
      lines.push('');
    }
  }

  if (
    digest.globalWarnings.length === 0 &&
    digest.artifactWarnings.length === 0 &&
    digest.subscriptions.length === 0
  ) {
    lines.push('_No non-green subscriptions or warnings found._', '');
  }

  return lines.join('\n');
}

export function registryAttentionDigestFilename(manifestVersion: string): string {
  const safe = manifestVersion.replace(/[^a-z0-9.-]+/gi, '-');
  return `consumer-contract-registry-attention-${safe}.md`;
}
